import type { ReactNode } from "react";

import { Icon } from "@core/components/Icon";
import { LinkBehaviour } from "@core/components/LinkBehaviour";
import type { SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classnames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";

import styles from "./Link.module.scss";

export const LINK_SIZES = ["lg", "md", "sm"] as const;
export type LinkSize = (typeof LINK_SIZES)[number];

type LinkBaseProps = {
  children: ReactNode;
  size?: LinkSize;
  iconBefore?: DirectionalIconComponent;
  iconAfter?: DirectionalIconComponent;
  disabled?: boolean;
  isInverse?: boolean;
  isMuted?: boolean;
  isStandalone?: boolean;
};

export type LinkAnchorProps = LinkBaseProps &
  SafeHtmlAttributes<HTMLAnchorElement> & {
    href: string;
    target?: string;
    rel?: string;
    nativeBehaviour?: boolean;
  };

export type LinkButtonProps = LinkBaseProps &
  SafeHtmlAttributes<HTMLButtonElement> & {
    href?: never;
    type?: "button" | "submit" | "reset";
    target?: never;
    rel?: never;
    nativeBehaviour?: never;
  };

export type LinkProps = LinkAnchorProps | LinkButtonProps;

const iconSizeMap: Record<LinkSize, "xs" | "sm"> = {
  lg: "sm",
  md: "sm",
  sm: "xs",
};

const isLinkAnchorProps = (props: LinkProps): props is LinkAnchorProps => typeof props.href === "string";

type SharedLinkProps = Pick<
  LinkBaseProps,
  "children" | "size" | "iconBefore" | "iconAfter" | "disabled" | "isInverse" | "isMuted" | "isStandalone"
>;

const getLinkClassName = ({
  size = "md",
  disabled = false,
  isInverse = false,
  isMuted = false,
  isStandalone = false,
}: Omit<SharedLinkProps, "children" | "iconBefore" | "iconAfter">) =>
  classnames(
    styles.link,
    styles[size],
    isInverse && styles.inverse,
    isMuted && styles.muted,
    isStandalone && styles.standalone,
    disabled && styles.disabled
  );

const renderLinkContent = ({
  children,
  size = "md",
  iconBefore: IconBefore,
  iconAfter: IconAfter,
}: SharedLinkProps) => (
  <>
    {IconBefore && <Icon icon={IconBefore} size={iconSizeMap[size]} aria-hidden="true" className={styles.iconBefore} />}
    <span className={styles.label}>{children}</span>
    {IconAfter && <Icon icon={IconAfter} size={iconSizeMap[size]} aria-hidden="true" className={styles.iconAfter} />}
  </>
);

export const Link = (props: LinkProps) => {
  if (isLinkAnchorProps(props)) {
    const {
      href,
      target,
      rel,
      nativeBehaviour,
      children,
      size = "md",
      iconBefore,
      iconAfter,
      disabled = false,
      isInverse = false,
      isMuted = false,
      isStandalone = false,
      ...anchorRest
    } = props;

    const sharedClassName = getLinkClassName({ size, disabled, isInverse, isMuted, isStandalone });
    const content = renderLinkContent({ children, size, iconBefore, iconAfter });

    return (
      <LinkBehaviour
        href={href}
        target={target}
        rel={rel}
        nativeBehaviour={nativeBehaviour}
        className={sharedClassName}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        {...anchorRest}
      >
        {content}
      </LinkBehaviour>
    );
  }

  const {
    children,
    size = "md",
    iconBefore,
    iconAfter,
    disabled = false,
    isInverse = false,
    isMuted = false,
    isStandalone = false,
    type = "button",
    ...buttonRest
  } = props;

  const sharedClassName = getLinkClassName({ size, disabled, isInverse, isMuted, isStandalone });
  const content = renderLinkContent({ children, size, iconBefore, iconAfter });

  return (
    <button type={type} className={sharedClassName} disabled={disabled} {...buttonRest}>
      {content}
    </button>
  );
};
